import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import {
  selectAllChatRooms,
  selectAllChatRoomMessageSync,
  selectGroupUserById,
} from "features/user/slice";
import {
  ChatRoomType,
  ChatRoomMemberType,
  MessageType,
  MessageContentType,
  ChatRoomMessage,
} from "features/user/types";

export const DashboardMailPreview = () => {
  const play = usePlaySound();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const active = useSelector((state: RootState) => state.user.active);
  const chatRooms = useSelector(selectAllChatRooms);
  const allSync = useSelector(selectAllChatRoomMessageSync);

  const { unreadCount, previewSync, previewRoomType } = useMemo(() => {
    const userSyncs = allSync.filter((s) => s.uid === active?.uid);

    const unreadSyncs = userSyncs.filter(
      (sync) =>
        dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null),
    );

    const pool = unreadSyncs.length > 0 ? unreadSyncs : userSyncs.filter((s) => s.latestMessage);

    const sorted = [...pool].sort((a, b) =>
      dayjs(b.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(a.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ"),
      ),
    );

    const preview = sorted[0] ?? null;
    const room = preview ? chatRooms.find((r) => r.id === preview.id) : null;

    return {
      unreadCount: unreadSyncs.length,
      previewSync: preview,
      previewRoomType: room?.type ?? null,
    };
  }, [allSync, active?.uid, chatRooms]);

  const talkPartnerUid = useMemo(() => {
    if (previewRoomType !== ChatRoomType.TALK || !previewSync) return null;
    const room = chatRooms.find((r) => r.id === previewSync.id);
    if (!room) return null;
    const partner = room.members.find((m) => m.type === ChatRoomMemberType.PARENTS);
    return partner?.uid ?? null;
  }, [previewRoomType, previewSync, chatRooms]);

  const talkPartner = useSelector((state: RootState) =>
    talkPartnerUid ? selectGroupUserById(state, talkPartnerUid) : null,
  );

  const senderInfo = useMemo(() => {
    switch (previewRoomType) {
      case ChatRoomType.NOTIFICATION:
        return {
          icon: `${Constants.assetHost}/assets/images/app_pluzze_icon.png`,
          name: I18n.t("MSG_INEXUS_OFFICIAL"),
        };
      case ChatRoomType.CONTACT:
        return {
          icon: `${Constants.assetHost}/assets/images/app_official_icon.png`,
          name: I18n.t("MSG_INEXUS_OFFICIAL_CONTACT"),
        };
      case ChatRoomType.TALK:
        return {
          icon: talkPartner?.icon ?? `${Constants.assetHost}/assets/images/app_official_icon.png`,
          name: talkPartner?.display_name ?? "...",
        };
      default:
        return null;
    }
  }, [previewRoomType, talkPartner]);

  const messageExcerpt = useMemo(() => {
    const msg = previewSync?.latestMessage;
    if (!msg) return null;
    return truncate(getMessagePreviewText(msg, active?.display_name), 30) || null;
  }, [previewSync, active?.display_name]);

  const messageDate = useMemo(() => {
    const msg = previewSync?.latestMessage;
    if (!msg) return null;
    const d = dayjs(msg.created_at, "YYYY-MM-DDTHH:mm:ssZ");
    return d.isToday() ? d.format("HH:mm") : d.format("MM/DD");
  }, [previewSync]);

  const handleGoToMessages = () => {
    play();
    btnDelay(() => {
      dispatch(actions.updateDialog({ type: DialogType.USER_PROFILE, value: false }));
      navigate("/user?tab=message");
    });
  };

  return (
    <button className="type-inherit-font opacity-button w-full" onClick={handleGoToMessages}>
      <div
        className="flex-col-el w-full px-4 py-2 rounded-xl bg-white/60 border-2 border-solid border-beige-60 cursor-pointer"
        style={{ boxShadow: "0px 2px 0px #FFD1A1" }}
      >
        {/* タイトル行 */}
        <div className="flex-row-el items-center gap-2">
          <span className="material-symbols-outlined type-md text-textcolor-black">mail</span>
          <p className="type-text type-sm text-textcolor-black">
            {I18n.t("MSG_USER_PROFILE_MESSAGE")}
          </p>
          {unreadCount > 0 && (
            <div className="flex-col-center min-w-[28px] h-[28px] px-1 bg-danger rounded-full">
              <p className="text-body type-sm text-white">{unreadCount}</p>
            </div>
          )}
          <span className="material-symbols-outlined type-sm text-gray-100 !ml-auto">
            chevron_right
          </span>
        </div>

        {/* メッセージプレビュー */}
        {previewSync?.latestMessage && senderInfo ? (
          <div className="flex-row-el items-center gap-3 mt-2">
            <div
              className="flex-col-center bg-white rounded-full w-[40px] h-[40px] shrink-0 overflow-hidden"
              style={{
                filter: "drop-shadow(1px 1px 2px rgba(63, 74, 97, 0.3))",
              }}
            >
              <img
                src={senderInfo.icon}
                alt=""
                className="w-[85%] h-[85%] rounded-full object-contain"
              />
            </div>
            <div className="flex-col-el items-start overflow-hidden flex-1 min-w-0">
              <p className="type-text type-body text-gray-100 !text-left">{senderInfo.name}</p>
              <p className="type-text type-body text-textcolor-black !text-left text-body-ellipsis w-full">
                {messageExcerpt}
              </p>
            </div>
            <p className="type-text type-sm text-gray-100 shrink-0">{messageDate}</p>
          </div>
        ) : (
          <p className="type-text type-body text-gray-100 mt-2">
            {I18n.t("MSG_DASHBOARD_MAIL_EMPTY")}
          </p>
        )}
      </div>
    </button>
  );
};

const getMessagePreviewText = (msg: ChatRoomMessage, displayName?: string): string => {
  // message_type ベースの抽出（自動返信など message_content にテキストがないケース）
  switch (msg.message_type) {
    case MessageType.CONTACT_INITIAL:
      return I18n.t("MSG_USER_PROFILE_MESSAGE_OFFICIAL_WELCOME_TEXT_0", {
        name: displayName ?? "",
      });
    case MessageType.CONTACT_INPUT_AUTO_REPLY:
    case MessageType.KIDS_REVIEW_FEEDBACK_INPUT_AUTO_REPLY:
      return I18n.t("MSG_USER_PROFILE_MESSAGE_CONTACT_INPUT_AUTO_REPLY");
    case MessageType.KIDS_REVIEW_RATING_AUTO_REPLY:
      return I18n.t("MSG_USER_PROFILE_MESSAGE_REVIEW_RATING_AUTO_REPLY");
    case MessageType.KIDS_REVIEW_FEEDBACK_AUTO_REPLY:
      return I18n.t("MSG_USER_PROFILE_MESSAGE_REVIEW_FEEDBACK_AUTO_REPLY");
    default:
      break;
  }

  // message_content_type ベースの抽出
  switch (msg.message_content_type) {
    case MessageContentType.IMAGE:
      return `[${I18n.t("MSG_DASHBOARD_MAIL_IMAGE")}]`;
    case MessageContentType.PROJECT:
      return `[${I18n.t("MSG_DASHBOARD_MAIL_PROJECT")}]`;
    case MessageContentType.COURSE_CLEAR:
      return extractText(msg.message_content) || `[${I18n.t("MSG_DASHBOARD_MAIL_COURSE_CLEAR")}]`;
    default:
      return extractText(msg.message_content);
  }
};

const extractText = (content: unknown): string => {
  if (!content) return "";
  if (typeof content === "string") return content;
  const c = content as Record<string, unknown>;
  const raw = c.text ?? c.detail ?? c.content ?? c.title ?? "";
  return typeof raw === "string" ? raw : "";
};

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}...` : text;
